import { type MouseEvent, useEffect } from 'react';

import { Button } from '../../server/Button/Button';
import { CircledIconButton } from '../../server/CircledIconButton/CircledIconButton';
import { classNames } from '../../server/utils/classNames';
import { type BannerAnalyticsData, useBannerShownAnalytics } from '../AnalyticsProvider/hooks/useBannerShownAnalytics';

export type StatusBannerVariant = 'info' | 'warning' | 'alert' | 'success';

export type CtaProps = {
	label: string;
	onClick: (event: MouseEvent<HTMLButtonElement>) => void;
};

export type StatusBannerProps = {
	analyticsData: BannerAnalyticsData;
	title?: string;
	content: string;
	variant: StatusBannerVariant;
	className?: string;
	ctaProps?: CtaProps;
	/** If passed in, renders a close button on the top right of the banner */
	onDismiss?: (event: MouseEvent<HTMLButtonElement>) => void;
};

export const StatusBanner = ({
	analyticsData,
	title,
	content,
	variant,
	className,
	ctaProps,
	onDismiss,
}: StatusBannerProps) => {
	const { unique_id: analyticsId } = analyticsData ?? {};
	const { handleBannerShown } = useBannerShownAnalytics(analyticsData);

	// send analytics on mount
	useEffect(() => handleBannerShown(), [handleBannerShown]);

	const containerStyle = classNames(
		'w-full flex items-start justify-between overflow-hidden bg-bg-raised-primary border border-border-primary rounded-lg',
		className,
	);

	const colorStripeStyle = classNames(
		'inline-block w-1 min-h-1 h-full rounded-full flex-shrink-0',
		STRIPE_COLOR_MAP[variant],
	);

	return (
		<div className={containerStyle}>
			<div className="h-full flex items-start gap-4 p-4">
				<div className={colorStripeStyle} />
				<div className="flex items-center gap-4">
					<div className="flex flex-col gap-3 flex-1">
						<div className="flex flex-col gap-1 flex-1">
							{title && (
								<p className="text-label" data-testid="status-banner-title">
									{title}
								</p>
							)}
							<p className="text-body" data-testid="status-banner-content">
								{content}
							</p>
						</div>
						{ctaProps && (
							<Button
								variant="actionLink"
								className="h-4 self-start text-label-sm"
								analyticsId={`${analyticsId}_cta_click`}
								onClick={ctaProps.onClick}>
								{ctaProps.label}
							</Button>
						)}
					</div>
				</div>
			</div>
			{onDismiss && (
				<CircledIconButton
					analyticsId={`${analyticsId}_dismiss_banner`}
					icon="micro_close"
					ariaLabel="Close"
					onClick={onDismiss}
					className="mr-2 mt-2"
				/>
			)}
		</div>
	);
};

const STRIPE_COLOR_MAP: Record<StatusBannerVariant, string> = {
	info: 'bg-accent-blue-300',
	success: 'bg-accent-green-400',
	warning: 'bg-util-caution-400',
	alert: 'bg-util-alert-400',
};
